import {
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import { get, post } from '../Services/http';
import { EditorStringModel } from '../Types/EditorString';
import Product from '../Types/Product';
import { OrderRow } from '../Types/OrderRow';
import { addToDataLayer } from '../Services/Cart.service';
import { AddToCartParams } from '../Components/WishList/types/WishlistTypes';
import {
  handleApiResponse,
  mapToAddTGtmItem,
} from '../Components/WishList/services/WishlistService';

interface WishListState {
  id: string;
  total: number;
  description?: EditorStringModel;
  items: WishListItem[];
  addToWishListAnimating: boolean;
  errors?: string[];
}

export interface WishListProps {
  id: string;
  title?: string;
  type?: string;
  description?: EditorStringModel;
  items?: WishListItem[];
  total?: number;
}

export interface WishListItem {
  id: string;
  listId: string;
  variantId?: string;
  sortIndex: string;
  quantity: number;
  product: Product;
  itemTotal: number;
  selectedVariantId?: string;
}

export interface AddAllToCartResponse {
  notInStockVariantIds: string[];
  orderRowsAdded: OrderRow[];
}

const DEFAULT_STATE: WishListState = {
  id: '',
  total: 0,
  items: [],
  addToWishListAnimating: false,
};

const mapToAddToWishListItem = (item: Product, quantity?: number) => {
  return { articleNumber: item.id, quantity: quantity ? quantity : 1 };
};

const mapToUpdateToWishListItem = (
  id: string,
  previousId?: string,
  quantity?: number
) => {
  return {
    articleNumber: id,
    previousArticleNumber: previousId,
    quantity: quantity ?? 1,
  };
};

export const addProductToWishList = createAsyncThunk(
  'wishlist/add',
  async (item: Product) => {
    const response = await post(
      '/api/wishlist/add',
      mapToAddToWishListItem(item)
    );
    return await response.json();
  }
);

export const updateWishlist = createAsyncThunk(
  'wishlist/update',
  async (state: WishListProps) => {
    const response = await post('/api/wishlist/update', state);
    return await handleApiResponse(response);
  }
);

export const shareWishlist = async () => {
  const response = await get('/api/wishlist/share');
  const data = await handleApiResponse(response);
  return data;
};

export const updateWishlistItem = createAsyncThunk(
  'wishlist/updateItem',
  async ({
    id,
    previousId,
    quantity,
  }: {
    id: string;
    previousId?: string;
    quantity?: number;
  }) => {
    const response = await post(
      '/api/wishlist/updateItem',
      mapToUpdateToWishListItem(id, previousId, quantity)
    );
    return await handleApiResponse(response);
  }
);

export const removeProductFromWishList = createAsyncThunk(
  'wishlist/remove',
  async (item: Product) => {
    const response = await post(
      '/api/wishlist/remove',
      mapToAddToWishListItem(item)
    );
    return await response.json();
  }
);

export const loadWishList = createAsyncThunk(
  'wishlist/load',
  async (_, { dispatch }) => {
    const response = await get('/api/wishlist');
    const data = await handleApiResponse(response);
    dispatch(receiveWishList(data));
    return data;
  }
);

export const addToCart = createAsyncThunk<
  AddAllToCartResponse,
  AddToCartParams
>(
  'wishlist/addToCart',
  async ({ wishlistItems, email, clickSource }, thunkAPI) => {
    const response = await post('/api/wishlist/addtocart', null);
    const data = await handleApiResponse(response);

    try {
      addToDataLayer(
        wishlistItems.map((i) => mapToAddTGtmItem(i)),
        data.orderRows,
        email,
        clickSource
      );
    } catch (err) {
      console.log(err);
    }

    thunkAPI.dispatch(receiveWishlistErrors(data.notInStockVariantIds));

    return data;
  }
);

const WishListSlice = createSlice({
  name: 'wishList',
  initialState: DEFAULT_STATE,
  reducers: {
    removeWishListItem: (
      state,
      action: PayloadAction<{ product: Product }>
    ) => {
      state.items = state.items.filter(
        (item) => item.product.id !== action.payload.product.id
      );
    },
    addWishListItem: (
      state,
      action: PayloadAction<{ product: Product }>
    ) => {
      state.items.push({
        id: action.payload.id,
        listId: action.payload.listId,
        variantId: action.payload.variantId,
        sortIndex: action.payload.sortIndex,
        quantity: 0,
        itemTotal: 0,
        product: action.payload.product,
      });
    },
    updateWishListItemQty: (
      state,
      action: PayloadAction<{ articleNumber: number; quantity: number }>
    ) => {
      var item = state.items.find(
        (item) =>
          item.product.articleNumber === action.payload.articleNumber
      );

      if (item) {
        item.quantity = action.payload.quantity;
        item.itemTotal =
          action.payload.quantity * item.product.fromPrice.fromPrice.price;
      }
    },
    receiveWishList: (state, action: PayloadAction<WishListProps>) => {
      return {
        ...state,
        ...action.payload,
      };
    },
    updateSelectedVariant: (
      state,
      action: PayloadAction<{ articleNumber?: number; variantId?: string }>
    ) => {
      var item = state.items.find(
        (item) =>
          item.product.articleNumber === action.payload.articleNumber
      );

      if (item) item.selectedVariantId = action.payload.variantId;
    },
    receiveWishlistErrors: (state, action: PayloadAction<string[]>) => {
      state.errors = action.payload;
    },
  },
});

export const {
  removeWishListItem,
  addWishListItem,
  receiveWishList,
  updateSelectedVariant,
  updateWishListItemQty,
  receiveWishlistErrors,
} = WishListSlice.actions;

export default WishListSlice.reducer;
